import moment from 'moment';

import {
  MarketableLocationHoursOfOperation,
  MarketableLocationHoursPeriod,
  MarketableLocation,
  CustomProperty
} from 'Src/types';

/**
 *
 * @param format e.g. dddd | ddd
 * @returns
 */
export const getWeekdays = (format: string) =>
  new Array(7).fill(null).map((_, i) => {
    return moment(i, 'e')
      .startOf('week')
      .isoWeekday(i + 1)
      .format(format);
  });

/**
 * Get a list of formatted hours with open and close times
 * @param {MarketableLocationHoursOfOperation} hours
 */
export const getFormattedHours = (
  hours: MarketableLocationHoursOfOperation
) => {
  const weekdays = getWeekdays('dddd');
  const weekdaysShort = getWeekdays('ddd');
  const currentDay = moment().format('dddd');

  const periodsByDay = (hours.periods ?? []).reduce<
    Record<string, MarketableLocationHoursPeriod>
  >((acc, period) => {
    acc[period.openDay] = period;
    return acc;
  }, {});

  return weekdays.map((day, index) => {
    const period: MarketableLocationHoursPeriod | undefined = periodsByDay[day];

    return {
      day,
      shortDay: weekdaysShort[index],
      openTime: period?.openTime
        ? moment(period.openTime, 'HH:mm').format('h:mm A')
        : undefined,
      closeTime: period?.closeTime
        ? moment(period.closeTime, 'HH:mm').format('h:mm A')
        : undefined,
      open: period?.openTime,
      today: day === currentDay
    };
  });
};

/**
 * Get the open/close time for today
 * @param {MarketableLocationHoursOfOperation} hours
 */
export const getTodaysHours = (hours: MarketableLocationHoursOfOperation) => {
  const currentDay = moment().format('dddd');

  const todaysPeriod = (hours.periods ?? []).find(
    (period) => period.openDay === currentDay
  );

  if (!todaysPeriod) return;

  const format = 'HH:mm';
  const startTime = moment(todaysPeriod.openTime, format);
  const endTime = moment(todaysPeriod.closeTime, format);
  const open = moment(moment(), format).isBetween(startTime, endTime);

  return {
    currentDay,
    openTime: moment(todaysPeriod.openTime, 'HH:mm').format('h:mm A'),
    closeTime: moment(todaysPeriod.closeTime, 'HH:mm').format('h:mm A'),
    open
  };
};

/**
 * Determines whether the about section should be rendered for a given location.
 * The about section should not be rendered if there are no contacts, hours of operation,
 * accessibility information, location description, or video media available for the location.
 *
 * @param location - The marketable location object.
 * @returns A boolean indicating whether the about section should be rendered.
 */
export const shouldNotRenderAboutSection = (location: MarketableLocation) => {
  const {
    location_description,
    hours_of_operation,
    contacts,
    media,
    accessibility
  } = location;
  const videoMedia = media?.find((item) => item.media_type === 'video');

  const showContacts = contacts && contacts.length > 0;
  const showHours =
    !!hours_of_operation && (hours_of_operation.periods ?? []).length > 0;
  const showAccessibility = !!accessibility && accessibility.length > 0;
  const showDescription = !!location_description;
  const showVideo = !!videoMedia;

  return (
    !showContacts &&
    !showHours &&
    !showAccessibility &&
    !showDescription &&
    !showVideo
  );
};

/**
 * Determines whether the map section should be rendered for a given location.
 * The map section should not be rendered if the location does not have valid latitude and longitude coordinates.
 *
 * @param location - The marketable location object.
 * @returns A boolean indicating whether the map section should be rendered.
 */
export const shouldRenderLocationSection = (location: MarketableLocation) => {
  const {
    address: { coordinates }
  } = location;
  return !!coordinates?.lat || !!coordinates.lng;
};

/**
 * Get a list of specialties for a given location
 * @param location - The marketable location object.
 * @returns A list of specialties in the format { name: string }
 */
export const getSpecialtiesList = (location: MarketableLocation) => {
  if (!location || !location.specialties) return [];

  return location.specialties.map((specialty) => ({
    name: specialty.specialty
  }));
};

/**
 * Check if a location's custom field matches a specific value
 * @param location - The marketable location object
 * @param customProperty - The custom property key to check
 * @param fieldValue - The expected value to match against
 * @returns True if the custom field value matches (case-insensitive), false otherwise
 */
export const locationHasCustomValue = ({
  location,
  customProperty,
  value
}: {
  location?: MarketableLocation;
  customProperty: CustomProperty;
  value: string;
}): boolean => {
  return (
    location?.custom?.[customProperty]?.toLowerCase()?.trim() ===
    value.toLowerCase()
  );
};

/**
 * Check if a location has Aggregated Availability enabled
 * @param location - The marketable location object
 * @returns boolean
 */
export const showLocationAggregatedAvailability = (
  location?: MarketableLocation
): boolean => {
  return locationHasCustomValue({
    location,
    customProperty: 'show_aggregated_availability',
    value: 'Yes'
  });
};
/** check if input is a string containing HTML tags
 * @param text - The input to check
 * @returns True if the input is a string containing HTML tags
 */
export const detectRichText = (text?: unknown): boolean => {
  if (typeof text !== 'string') {
    return false;
  }
  const richTextRegex = /<\/?[a-zA-Z][^>]*>/i;
  return richTextRegex.test(text);
};
